import { Global, useTheme } from "@emotion/react";
import AccessibleIcon from "@mui/icons-material/Accessible";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CircularProgress,
  Divider,
  Fab,
  Grid,
  Skeleton,
  Stack,
  SwipeableDrawer,
  Typography,
  styled,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useMemo, useState, useEffect, useRef } from "react";
import AudioPlayer from "../../components/AudioPlayer/AudioPlayer";
import Draggable from "react-draggable";

const drawerBleeding = 0;
const FloatingActionButton = styled(Fab)(({ theme }) => ({
  // backgroundColor:
  //   theme.palette.mode === "light"
  //     ? grey[100]
  //     : theme.palette.background.default,
  position: "fixed",
  bottom: 100,
  right: 100,
  // width: "100%",
  height: 60,
  textAlign: "center",
}));

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "light" ? "#fff" : grey[800],
}));

const CustomTypography = styled(Typography)(
  ({ theme, variant, font, size, customspacing }) => {
    const fontBase = theme.typography[variant].fontSize;
    return {
      fontFamily: font ? "OpenDyslexic-Regular" : "Roboto, sans-serif",
      fontSize: `calc(1.${(size ?? 1) - 1} * ${fontBase})`, //variant base
      wordSpacing: `calc(${(customspacing ?? 1) - 1} * .16em)`, //.16
      letterSpacing: `calc(${(customspacing ?? 1) - 1} * .12em)`, //-12
    };
  }
);

const Presentation = () => {
  const [open, setOpen] = useState(false);
  const [font, setFont] = useState(false);
  const [maskScreen, setMaskScreen] = useState(false);
  const [arrowLecture, setArrowLecture] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [textSize, setTextSize] = useState(1);
  const [spacingText, setSpacingText] = useState(1);
  const [audios, setAudios] = useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const toggleFont = () => {
    setFont((prev) => !prev);
  };

  const toggleAudios = () => {
    setAudios((prev) => !prev);
  };

  const toggleMaskScreen = () => {
    if (arrowLecture) setArrowLecture(false);
    setMaskScreen((prev) => !prev);
  };

  const toggleArrowLecture = () => {
    if (maskScreen) setMaskScreen(false);
    setArrowLecture((prev) => !prev);
  };

  const handleTextSize = () => {
    setTextSize((prev) => {
      if (prev === 4) return 1;
      return prev + 1;
    });
  };

  const handleSpacingText = () => {
    setSpacingText((prev) => {
      if (prev === 4) return 1;
      return prev + 1;
    });
  };

  useEffect(() => {
    const handleMouseMove = (event) => {
      setMousePos({ x: event.pageX, y: event.pageY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  // This is used only for the example
  const container = undefined;
  return (
    <Box sx={{ background: "inherit" }}>
      <AudioPlayer external={audios}></AudioPlayer>
      {maskScreen && (
        <>
          <div
            style={{
              position: "fixed",
              zIndex: "99",
              width: "100%",
              background: "rgba(0, 0, 0, 0.7)",
              top: "0px",
              height: mousePos.y > 100 ? mousePos.y - 100 : 0,
              left: 0,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "12px",
                position: "absolute",
                background: "#18529D",
                bottom: "0px",
              }}
            ></div>
          </div>
          <div
            data-uw-feature-ignore="true"
            style={{
              position: "fixed",
              zIndex: "99",
              width: "100%",
              background: "rgba(0, 0, 0, 0.7)",
              bottom: "0px",
              height: `calc(100vh - (${mousePos.y}px + 100px))`,
              left: 0,
            }}
          >
            <div
              data-uw-feature-ignore="true"
              style={{
                width: "100%",
                height: "12px",
                position: "absolute",
                background: "#28AD56",
                top: "0px",
              }}
            ></div>
          </div>
        </>
      )}
      {arrowLecture && (
        <Box
          sx={{
            position: "absolute",
            margin: "0em",
            padding: "0.8em",
            boxSizing: "border-box",
            background: "#18529D",
            boxShadow: "0px 3px 3px 0 rgba(0, 0, 0, 0.4)",
            minWidth: "1000px",
            zIndex: 2,
            top: `${mousePos.y}px`,
            left: `calc(${mousePos.x}px - 480px)`,
            "&:after": {
              content: '""',
              position: "absolute",
              width: 0,
              height: 0,
              marginLeft: "0em",
              top: 0,
              left: "50%",
              border: "0.7em solid black",
              borderColor: "transparent transparent #18529D #18529D",
              transformOrigin: "0 0",
              transform: "rotate(135deg)",
            },
          }}
        ></Box>
      )}

      <SwipeableDrawer
        container={container}
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={drawerBleeding}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: false,
        }}
      >
        <StyledBox
          sx={{
            position: "absolute",
            top: -drawerBleeding,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: "visible",
            right: 0,
            left: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            padding={8}
          >
            <Button
              variant="contained"
              onClick={toggleFont}
              sx={{ fontSize: 20, my: 4 }}
            >
              {font ? "Vision Normal" : "Visión con Dislexia"}
            </Button>
            <Button
              variant="contained"
              onClick={toggleMaskScreen}
              sx={{ fontSize: 20, my: 4 }}
            >
              {!maskScreen
                ? "Encender Mascara de Lectura"
                : "Apagar mascara de lectura"}
            </Button>
            <Button
              variant="contained"
              onClick={toggleArrowLecture}
              sx={{ fontSize: 20, my: 4 }}
            >
              {!arrowLecture
                ? "Encender Indicador de Lectura"
                : "Apagar Indicador de lectura"}
            </Button>
            <Button
              variant="contained"
              onClick={handleTextSize}
              sx={{ fontSize: 20, my: 4 }}
            >
              {`Aumentar Tamaño de Texto ${textSize}`}
            </Button>
            <Button
              variant="contained"
              onClick={handleSpacingText}
              sx={{ fontSize: 20, my: 4 }}
            >
              {`Aumentar Espaciado de Texto ${spacingText}`}
            </Button>
            <Button
              variant="contained"
              onClick={toggleAudios}
              sx={{ fontSize: 20, my: 4 }}
            >
              {audios ? "Apagar" : "Encender"} reproducción para discapacidad
              visual
            </Button>
          </Stack>
        </StyledBox>
      </SwipeableDrawer>
      <Global
        styles={{
          ".MuiDrawer-root > .MuiPaper-root": {
            height: `calc(50% - ${drawerBleeding}px)`,
            overflow: "visible",
          },
        }}
      />
      <FloatingActionButton
        color="primary"
        aria-label="add"
        onClick={toggleDrawer(true)}
      >
        <AccessibleIcon sx={{ fontSize: "2.8em" }} />
      </FloatingActionButton>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <CardActionArea component="a" href="#" sx={{ background: "inherit" }}>
            <Card sx={{ display: "flex", background: "inherit" }}>
              <CardContent
                sx={{
                  flex: 1,
                }}
              >
                <CustomTypography
                  component="h2"
                  variant="h3"
                  sx={{ pb: 2 }}
                  font={font ? 1 : 0}
                  size={textSize}
                  customspacing={spacingText}
                >
                  PRESENTACIÓN
                </CustomTypography>
                <CustomTypography
                  variant="h5"
                  paragraph
                  font={font ? 1 : 0}
                  size={textSize}
                  customspacing={spacingText}
                >
                  El Sistema de Enseñanza Abierta de la Universidad Veracruzana
                  es una institución educativa fundada en el año de 1980, época
                  en la que se desarrolló esta modalidad en diversas partes del
                  mundo y del país; actualmente cuenta con 15 centros escolares
                  distribuidos en cinco regiones (Xalapa, Veracruz, Poza
                  Rica-Tuxpan, Córdoba-Orizaba, Coatzacoalcos-Minatitlán) del
                  Estado de Veracruz; en los que se ofrecen sesiones educativas
                  de interacción grupal los fines de semana. El Modelo Educativo
                  Integral y Flexible del Sistema de Enseñanza Abierta permite a
                  los estudiantes avanzar gradualmente en su trayectoria
                  escolar, pudiendo elegir cargas mínimas de crédito por
                  semestre, que le permitirá concluir su carrera en cuatro años,
                  o cargas máximas, con lo que podrán terminar en menos de tres
                  años y medio. Para ingresar al Enseñanza Abierta de la
                  Universidad Veracruzana, es necesario haber concluido sus
                  estudios de nivel bachillerato y preinscribirse al examen de
                  admisión general que convoca la Universidad Veracruzana
                  durante el mes de febrero de cada año.
                </CustomTypography>
              </CardContent>
              <CardMedia
                component="img"
                sx={{
                  width: 160,
                  display: {
                    xs: "none",
                    sm: "block",
                    marginLeft: 16,
                    marginRight: 16,
                    objectFit: "contain",
                  },
                }}
                image={
                  "https://upload.wikimedia.org/wikipedia/commons/8/8f/Logo_de_la_Universidad_Veracruzana.svg"
                }
                alt={"image-label"}
              />
            </Card>
          </CardActionArea>
        </Grid>
        <Grid item xs={12}>
          <CardActionArea component="a" href="#" sx={{ background: "inherit" }}>
            <Card sx={{ display: "flex", background: "inherit" }}>
              <CardContent sx={{ flex: 1 }}>
                <CustomTypography
                  component="h2"
                  variant="h3"
                  sx={{ pb: 2 }}
                  font={font ? 1 : 0}
                  size={textSize}
                >
                  MISIÓN
                </CustomTypography>
                <CustomTypography
                  variant="h5"
                  paragraph
                  font={font ? 1 : 0}
                  size={textSize}
                >
                  El Sistema de Enseñanza Abierta, con funciones de docencia,
                  investigación, extensión y vinculación, con modalidad
                  semipresencial, tiene como misión ampliar el acceso a la
                  educación para formar profesionistas de manera responsable, a
                  nivel licenciatura, posgrado y educación continua, con alto
                  valor humano, inclusión y equidad de género, que agregue valor
                  a la sociedad, fomentando una cultura de desarrollo sostenible
                  y una relación estrecha con los sectores productivo y social,
                  con soluciones críticas y creativas en el desempeño
                  profesional y en la intervención de problemas actuales,
                  enfocadas al aseguramiento de la calidad educativa.
                </CustomTypography>
              </CardContent>
            </Card>
          </CardActionArea>
        </Grid>
        <Grid item xs={12}>
          <CardActionArea component="a" href="#" sx={{ background: "inherit" }}>
            <Card sx={{ display: "flex", background: "inherit" }}>
              <CardContent sx={{ flex: 1 }}>
                <CustomTypography
                  component="h2"
                  variant="h3"
                  sx={{ pb: 2 }}
                  font={font ? 1 : 0}
                  size={textSize}
                >
                  VISIÓN
                </CustomTypography>
                <CustomTypography
                  variant="h5"
                  paragraph
                  font={font ? 1 : 0}
                  size={textSize}
                >
                  Al 2025, el Sistema de Enseñanza Abierta ofrece programas
                  educativos reconocidos por su calidad nacional, en las áreas
                  de Pedagogía, el Derecho, la Sociología, Administración y
                  Contaduría, con una planta docente comprometida con sus
                  superación profesional que prepara a los estudiantes en las
                  competencias requeridas por las empresas públicas, privadas y
                  organizaciones no gubernamentales, con una visión humanista,
                  enfocadas al aseguramiento de la calidad educativa, que
                  propicie condiciones de equidad de género, inclusión,
                  responsabilidad social, desarrollo sostenible y la cultura de
                  paz, de acuerdo a las vocaciones y necesidades regionales.
                </CustomTypography>
              </CardContent>
            </Card>
          </CardActionArea>
        </Grid>
        <Grid item xs={12}>
          <CardActionArea component="a" href="#" sx={{ background: "inherit" }}>
            <Card sx={{ display: "flex", background: "inherit" }}>
              <CardContent sx={{ flex: 1 }}>
                <CustomTypography
                  component="h2"
                  variant="h3"
                  sx={{ pb: 2 }}
                  font={font ? 1 : 0}
                  size={textSize}
                >
                  ASESORIAS
                </CustomTypography>
                <CustomTypography
                  variant="h5"
                  paragraph
                  font={font ? 1 : 0}
                  size={textSize}
                >
                  El Sistema de Enseñanza Abierta cuenta con un sistema de
                  asesorías intersemanales, cubierto presencialmente en las CASA
                  SEA de Veracruz, asi como en espacios determinados por cita
                  agendada entre profesores y alumnos del SEA; o a través de
                  comunicación electrónica entre profesores y
                  estudiantes.TUTORÍAS El SEA ofrece un servicio de asistencia
                  de tutores académicos para guiar a los estudiantes en las
                  carreras que aplican el Modelo Educativo Integral Flexible:
                  Administración de Empresas, Contaduría, Derecho, Pedagogía y
                  Sociología. MATERIALES Y SERVICIOS Se desarrolla un proyecto
                  tecnológico para ofrecer servicios de cursos en línea de las
                  experiencias de licenciatura, así también se está generando la
                  elaboración de material didáctico a través de la edición de
                  libros electrónicos, disponibles a través de portales
                  electrónicos de los profesores del Sistema de Enseñanza
                  Abierta. En nuestra institución se fomenta el uso de
                  comunidades electrónicas pasra fortalecer los vínculos de
                  comunicación entre los estudiantes, los grupos a los que se
                  integran, los profesores y la administración escolar del
                  sistema.
                </CustomTypography>
              </CardContent>
            </Card>
          </CardActionArea>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Presentation;
